import type { DomRenderer } from '@blocksuite/affine-block-surface';
import type { ShapeElementModel } from '@blocksuite/affine-model';
import { DefaultTheme } from '@blocksuite/affine-model';
import { SVGShapeBuilder } from '@blocksuite/global/gfx';

import { manageClassNames, setStyles } from './utils';

function applyShapeSpecificStyles(
  model: ShapeElementModel,
  element: HTMLElement,
  zoom: number
) {
  // Reset properties that might be set by different shape types
  element.style.removeProperty('clip-path');
  element.style.removeProperty('border-radius');
  // Clear DOM for shapes that don't use SVG, or if type changes from SVG-based to non-SVG-based
  if (model.shapeType !== 'diamond' && model.shapeType !== 'triangle') {
    while (element.firstChild) element.firstChild.remove();
  }

  switch (model.shapeType) {
    case 'rect': {
      const w = model.w * zoom;
      const h = model.h * zoom;
      const r = model.radius ?? 0;
      const borderRadius =
        r < 1 ? `${Math.min(w * r, h * r)}px` : `${r * zoom}px`;
      element.style.borderRadius = borderRadius;
      break;
    }
    case 'ellipse':
      element.style.borderRadius = '50%';
      break;
    case 'diamond':
      element.style.clipPath = 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)';
      break;
    case 'triangle':
      element.style.clipPath = 'polygon(50% 0%, 100% 100%, 0% 100%)';
      break;
  }
  // No 'else' needed to clear styles, as they are reset at the beginning of the function.
}

function applyBorderStyles(
  model: ShapeElementModel,
  element: HTMLElement,
  strokeColor: string,
  zoom: number
) {
  element.style.border =
    model.strokeStyle !== 'none'
      ? `${model.strokeWidth * zoom}px ${model.strokeStyle === 'dash' ? 'dashed' : 'solid'} ${strokeColor}`
      : 'none';
}

function applyTransformStyles(model: ShapeElementModel, element: HTMLElement) {
  if (model.rotate && model.rotate !== 0) {
    setStyles(element, {
      transform: `rotate(${model.rotate}deg)`,
      transformOrigin: 'center',
    });
  } else {
    setStyles(element, {
      transform: '',
      transformOrigin: '',
    });
  }
}

function applyShadowStyles(
  model: ShapeElementModel,
  element: HTMLElement,
  renderer: DomRenderer
) {
  if (model.shadow) {
    const { offsetX, offsetY, blur, color } = model.shadow;
    setStyles(element, {
      boxShadow: `${offsetX}px ${offsetY}px ${blur}px ${renderer.getColorValue(color)}`,
    });
  } else {
    setStyles(element, { boxShadow: '' });
  }
}

/**
 * Renders a ShapeElementModel to a given HTMLElement using DOM properties.
 * This function is intended to be registered via the DomElementRendererExtension.
 *
 * @param model - The shape element model containing rendering properties.
 * @param element - The HTMLElement to apply the shape's styles to.
 * @param renderer - The main DOMRenderer instance, providing access to viewport and color utilities.
 */
export const shapeDomRenderer = (
  model: ShapeElementModel,
  element: HTMLElement,
  renderer: DomRenderer
): void => {
  const { zoom } = renderer.viewport;
  const unscaledWidth = model.w;
  const unscaledHeight = model.h;

  const fillColor = renderer.getColorValue(
    model.fillColor,
    DefaultTheme.shapeFillColor,
    true
  );
  const strokeColor = renderer.getColorValue(
    model.strokeColor,
    DefaultTheme.shapeStrokeColor,
    true
  );

  element.style.width = `${unscaledWidth * zoom}px`;
  element.style.height = `${unscaledHeight * zoom}px`;
  element.style.boxSizing = 'border-box';

  // Apply shape-specific clipping, border-radius, and potentially clear innerHTML
  applyShapeSpecificStyles(model, element, zoom);

  const isGrad = model.fillColor && typeof model.fillColor === 'string' && model.fillColor.startsWith('{');
  let grad: any = null;
  if (isGrad) {
    try {
      grad = JSON.parse(model.fillColor);
    } catch (e) {}
  }

  if (model.shapeType === 'diamond' || model.shapeType === 'triangle' || isGrad) {
    // For diamond, triangle, or gradient fills, we render using SVG
    element.style.border = 'none'; // Ensure no standard CSS border interferes
    element.style.backgroundColor = 'transparent'; // Host element is transparent

    const strokeW = model.strokeWidth;
    const finalStrokeColor =
      model.strokeStyle !== 'none' && strokeW > 0 ? strokeColor : 'transparent';
    const finalStrokeDasharray =
      model.strokeStyle === 'dash' && finalStrokeColor !== 'transparent'
        ? '12, 12'
        : 'none';

    // Build SVG safely with DOM-API
    const SVG_NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('viewBox', `0 0 ${unscaledWidth} ${unscaledHeight}`);
    svg.setAttribute('preserveAspectRatio', 'none');

    let finalFillColor = model.filled ? fillColor : 'transparent';

    if (isGrad && grad) {
      const defs = document.createElementNS(SVG_NS, 'defs');
      const gradId = `grad-${model.id}`;
      const gradEl = document.createElementNS(SVG_NS, grad.type === 'radial' ? 'radialGradient' : 'linearGradient');
      gradEl.setAttribute('id', gradId);

      // Set Stops
      (grad.stops || []).forEach((s: any) => {
        const stop = document.createElementNS(SVG_NS, 'stop');
        stop.setAttribute('offset', `${s.offset * 100}%`);
        stop.setAttribute('stop-color', s.color);
        gradEl.appendChild(stop);
      });

      if (grad.scope === 'overall' && grad.bounds) {
        // userSpaceOnUse coordinates relative to the group bounds
        gradEl.setAttribute('gradientUnits', 'userSpaceOnUse');
        const bounds = grad.bounds;
        const w_group = bounds.maxX - bounds.minX;
        const h_group = bounds.maxY - bounds.minY;
        const cx_abs = bounds.minX + w_group / 2;
        const cy_abs = bounds.minY + h_group / 2;

        if (grad.type === 'radial') {
          const r_abs = Math.sqrt(w_group * w_group + h_group * h_group) / 2;
          gradEl.setAttribute('cx', String(cx_abs - model.x));
          gradEl.setAttribute('cy', String(cy_abs - model.y));
          gradEl.setAttribute('r', String(r_abs));
        } else {
          const angleRad = ((grad.angle !== undefined ? grad.angle : 90) * Math.PI) / 180;
          const d = 0.5 * (w_group * Math.abs(Math.cos(angleRad)) + h_group * Math.abs(Math.sin(angleRad)));
          
          const x1_abs = cx_abs - d * Math.cos(angleRad);
          const y1_abs = cy_abs - d * Math.sin(angleRad);
          const x2_abs = cx_abs + d * Math.cos(angleRad);
          const y2_abs = cy_abs + d * Math.sin(angleRad);

          gradEl.setAttribute('x1', String(x1_abs - model.x));
          gradEl.setAttribute('y1', String(y1_abs - model.y));
          gradEl.setAttribute('x2', String(x2_abs - model.x));
          gradEl.setAttribute('y2', String(y2_abs - model.y));
        }
      } else {
        // objectBoundingBox coordinates (default relative to 0-1)
        gradEl.setAttribute('gradientUnits', 'objectBoundingBox');
        if (grad.type === 'radial') {
          gradEl.setAttribute('cx', '0.5');
          gradEl.setAttribute('cy', '0.5');
          gradEl.setAttribute('r', '0.5');
        } else {
          const angleRad = ((grad.angle !== undefined ? grad.angle : 90) * Math.PI) / 180;
          const x1 = 0.5 - 0.5 * Math.cos(angleRad);
          const y1 = 0.5 - 0.5 * Math.sin(angleRad);
          const x2 = 0.5 + 0.5 * Math.cos(angleRad);
          const y2 = 0.5 + 0.5 * Math.sin(angleRad);
          gradEl.setAttribute('x1', String(x1));
          gradEl.setAttribute('y1', String(y1));
          gradEl.setAttribute('x2', String(x2));
          gradEl.setAttribute('y2', String(y2));
        }
      }

      defs.appendChild(gradEl);
      svg.appendChild(defs);
      finalFillColor = `url(#${gradId})`;
    }

    let shapeChild: SVGElement;
    if (model.shapeType === 'ellipse') {
      const rx = (unscaledWidth - strokeW) / 2;
      const ry = (unscaledHeight - strokeW) / 2;
      shapeChild = document.createElementNS(SVG_NS, 'ellipse');
      shapeChild.setAttribute('cx', String(unscaledWidth / 2));
      shapeChild.setAttribute('cy', String(unscaledHeight / 2));
      shapeChild.setAttribute('rx', String(rx > 0 ? rx : 0));
      shapeChild.setAttribute('ry', String(ry > 0 ? ry : 0));
    } else if (model.shapeType === 'rect') {
      const r = model.radius ?? 0;
      const borderRadius = r < 1 ? Math.min(unscaledWidth * r, unscaledHeight * r) : r;
      shapeChild = document.createElementNS(SVG_NS, 'rect');
      shapeChild.setAttribute('x', String(strokeW / 2));
      shapeChild.setAttribute('y', String(strokeW / 2));
      shapeChild.setAttribute('width', String(unscaledWidth - strokeW > 0 ? unscaledWidth - strokeW : 0));
      shapeChild.setAttribute('height', String(unscaledHeight - strokeW > 0 ? unscaledHeight - strokeW : 0));
      shapeChild.setAttribute('rx', String(borderRadius));
      shapeChild.setAttribute('ry', String(borderRadius));
    } else if (model.shapeType === 'diamond') {
      shapeChild = document.createElementNS(SVG_NS, 'polygon');
      shapeChild.setAttribute('points', SVGShapeBuilder.diamond(unscaledWidth, unscaledHeight, strokeW));
    } else {
      // triangle
      shapeChild = document.createElementNS(SVG_NS, 'polygon');
      shapeChild.setAttribute('points', SVGShapeBuilder.triangle(unscaledWidth, unscaledHeight, strokeW));
    }

    shapeChild.setAttribute('fill', finalFillColor);
    shapeChild.setAttribute('stroke', finalStrokeColor);
    shapeChild.setAttribute('stroke-width', String(strokeW));
    if (finalStrokeDasharray !== 'none') {
      shapeChild.setAttribute('stroke-dasharray', finalStrokeDasharray);
    }
    svg.appendChild(shapeChild);

    // Replace existing children to avoid memory leaks
    element.replaceChildren(svg);
  } else {
    // Standard rendering for other shapes (e.g., rect, ellipse)
    element.style.backgroundColor = model.filled ? fillColor : 'transparent';
    applyBorderStyles(model, element, strokeColor, zoom); // Uses standard CSS border
  }

  applyTransformStyles(model, element);

  element.style.zIndex = renderer.layerManager.getZIndex(model).toString();

  manageClassNames(model, element);
  applyShadowStyles(model, element, renderer);
};
